import { CustomComponentProps } from '@droplet-tech-code/core-common-types';
import { FormInput } from '@droplet-tech-code/core-elements/module/ui/Inputs/FormInputs/FormInput';
import { useNumberForm } from '@droplet-tech-code/core-elements/module/ui/Inputs/NumberInput/NumberInput.hook';
import { formatNumber } from '@droplet-tech-code/core-elements/module/ui/Inputs/NumberInput/NumberInput.utils';

import { AppText } from '~/components/Text';

/**
 * A number input that renders a currency code/symbol to the left of the field
 * (inside the input box) rather than only in the label. Behaves exactly like
 * the built-in FormBuilder `number` input — it reuses the same form hook so
 * validation, `multipleOf` minor-unit conversion and form registration all
 * carry over. Config `meta` carries the number options:
 *   meta: { currency: string; multipleOf?: number; initialValue?: number; format?: 'commas' | 'currency' }
 */
export const FormCurrencyNumber = (props: CustomComponentProps<any>) => {
  const meta = (props.config.meta ?? {}) as {
    currency?: string;
    multipleOf?: number;
    initialValue?: number;
    format?: 'commas' | 'currency';
  };

  // Re-present the custom config as a `number` config so the shared number
  // hook can drive it.
  const numberProps: any = {
    ...props,
    config: {
      ...props.config,
      type: 'number',
      multipleOf: meta.multipleOf,
      initialValue: meta.initialValue,
      format: meta.format,
    },
  };

  const {
    value,
    setValue,
    isValid,
    feedback,
    error,
    disabled,
    label,
    optional,
    readonly,
    config,
    noFeedback,
  } = useNumberForm(numberProps);

  const numValue = formatNumber(value, config.format);

  return (
    <FormInput
      rowProps={(props as any).rowProps}
      noFeedback={noFeedback}
      feedbackOnlyWithError={config.feedbackOnlyWithError}
      autoCorrect={false}
      autoCapitalize="none"
      keyboardType="number-pad"
      value={numValue}
      disabled={disabled}
      label={label}
      optional={optional}
      readonly={readonly}
      iconStart={
        meta.currency ? (
          <AppText.Body2Regular color="monochrome-mid">
            {meta.currency}
          </AppText.Body2Regular>
        ) : undefined
      }
      onChangeText={(txt) => {
        setValue(txt);
      }}
      feedback={feedback}
      error={error}
      onBlur={() => {
        isValid({ showFeedback: true, onBlur: true });
      }}
    />
  );
};
